import { FlagGlyph, PriorityGlyph, StatusGlyph, type StatusGroup } from "../glyph";
import { hueVars, type HueOrNeutral } from "../hue";
import { cx } from "../tv";

/** The hue of each status group (DESIGN-RULES 8). Custom statuses inside a group reuse it. */
export const STATUS_HUE: Record<StatusGroup, HueOrNeutral> = {
  backlog: "neutral",
  todo: "neutral",
  progress: "sky",
  review: "saffron",
  done: "fern",
  cancelled: "neutral",
};

/** Soft pill with the glyph and the status name in sentence case. */
export function StatusPill({ group, label, className }: { group: StatusGroup; label: string; className?: string }) {
  return (
    <span
      style={hueVars(STATUS_HUE[group])}
      className={cx(
        "inline-flex h-6 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-pill bg-hue-subtle ps-1.5 pe-2.5 text-sm font-semibold text-hue-text",
        STATUS_HUE[group] !== "neutral" && "shadow-[inset_0_0_0_1px_color-mix(in_oklch,var(--hue-solid)_22%,transparent)]",
        className,
      )}
    >
      <StatusGlyph group={group} size={14} />
      {label}
    </span>
  );
}

export function StatusIcon({ group, label, size }: { group: StatusGroup; label: string; size?: number }) {
  return <StatusGlyph group={group} size={size} label={label} />;
}

export function PriorityIcon({ priority, label, size }: { priority: string; label: string; size?: number }) {
  return <PriorityGlyph priority={priority} size={size} label={label} />;
}

/** Blocked or waiting flag: Ember chip with the reason and its age, for example "Client · 3d" (DESIGN-RULES 8). */
export function BlockedFlag({ reason, age, className }: { reason: string; age?: string; className?: string }) {
  return (
    <span className={cx("inline-flex h-[21px] shrink-0 items-center gap-1 whitespace-nowrap rounded-pill bg-blocked-bg px-2 text-xs font-semibold text-blocked-text", className)}>
      <FlagGlyph size={11} />
      {age ? `${reason} · ${age}` : reason}
    </span>
  );
}

export type HealthTone = "good" | "watch" | "risk";
const HEALTH_HUE: Record<HealthTone, HueOrNeutral> = { good: "fern", watch: "saffron", risk: "ember" };

/** Green, amber or red with a one-line reason (the reason goes in a tooltip by the caller). */
export function HealthBadge({ tone, label, className }: { tone: HealthTone; label: string; className?: string }) {
  return (
    <span
      style={hueVars(HEALTH_HUE[tone])}
      className={cx("inline-flex h-6 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-pill bg-hue-subtle px-2.5 text-sm font-semibold text-hue-text shadow-[inset_0_0_0_1px_var(--hue-border)]", className)}
    >
      <i aria-hidden className="size-1.5 rounded-pill bg-hue" />
      {label}
    </span>
  );
}
